import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Shirt, Plus, ShoppingCart, Edit, Trash2, Search, X, Upload, Loader2, Package,
} from "lucide-react";
import {
  listUniforms, listStudentsForSale, upsertUniform, deleteUniform, createUniformOrder,
  listAvailableJerseyNumbers,
} from "@/lib/api/uniforms.functions";
import { lookupBuyerByCpf } from "@/lib/api/sales.functions";
import { PAYMENT_METHODS, paymentMethodLabel, isMercadoPagoMethod, isPresencialMethod, isComprovanteMethod } from "@/lib/payment-methods";
import { MercadoPagoBadge } from "@/components/shared/MercadoPagoBadge";
import { PaymentMethodsPicker } from "@/components/shared/PaymentMethodsPicker";
import { Switch } from "@/components/ui/switch";
import { SaleWhatsappBillingModal, type SaleBillingInput } from "@/components/sales/SaleWhatsappBillingModal";
import { brIsoDate } from "@/lib/tz";
import { SIZES_CATALOG, CAMISA_GUIDE, CALCAO_GUIDE, CAMISA_SHORT_GUIDE } from "@/lib/uniform-sizes";


export const Route = createFileRoute("/_authenticated/m/uniformes")({
  component: UniformesPage,
});

type SizeRow = { id?: string | null; size: string; width: number | null; height: number | null; stock: number };
type Uniform = {
  id: string; name: string; piece_type: string; price: number; stock: number | null;
  photo_url: string | null; uniform_sizes: SizeRow[];
  accepted_payment_methods: string[] | null;
  max_jersey_number: number | null;
  require_jersey_name: boolean;
  require_jersey_number: boolean;
  jersey_uniqueness: "none" | "category" | "ct";
};
type Student = { id: string; name: string; photo_url: string | null; parent?: { name: string | null; phone: string | null; email: string | null } | null };

const PIECE_TYPES = [
  { value: "camisa", label: "Camisa" },
  { value: "calcao", label: "Calção / Shorts" },
  { value: "camisa_short", label: "Camisa + Short" },
  { value: "meiao", label: "Meião" },
  { value: "agasalho", label: "Agasalho" },
  { value: "outro", label: "Outro" },
];

const MEIAO_CLASSES = [
  { value: "infantil", label: "Infantil", size: "29-33", full: "Infantil 29–33" },
  { value: "juvenil", label: "Juvenil", size: "34-38", full: "Juvenil 34–38" },
  { value: "adulto", label: "Adulto", size: "39-44", full: "Adulto 39–44" },
];

// Tabelas de medidas (largura x comprimento, em cm)
const SIZE_GUIDE: Record<string, Record<string, { width: number; height: number; label?: string }>> = {
  camisa: CAMISA_GUIDE,
  calcao: CALCAO_GUIDE,
  camisa_short: CAMISA_SHORT_GUIDE,
};


function suggest(pieceType: string, size: string) {
  const guide = SIZE_GUIDE[pieceType];
  if (!guide) return null;
  return guide[size] ?? null;
}

const fmtMoney = (n: number) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n || 0);

function UniformesPage() {
  const listFn = useServerFn(listUniforms);
  const listStudentsFn = useServerFn(listStudentsForSale);
  const saveFn = useServerFn(upsertUniform);
  const delFn = useServerFn(deleteUniform);
  const sellFn = useServerFn(createUniformOrder);

  const [loading, setLoading] = useState(true);
  const [uniforms, setUniforms] = useState<Uniform[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [tcId, setTcId] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<Uniform | null>(null);

  const [saleOpen, setSaleOpen] = useState(false);
  const [saleUniform, setSaleUniform] = useState<Uniform | null>(null);
  const [billing, setBilling] = useState<SaleBillingInput | null>(null);

  const reload = async () => {
    setLoading(true);
    try {
      const [u, s] = await Promise.all([listFn(), listStudentsFn()]);
      setUniforms((u.uniforms ?? []) as any);
      setTcId(u.tcId);
      setStudents((s.students ?? []) as any);
    } catch (e: any) {
      toast.error(e?.message ?? "Erro ao carregar");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(
    () => uniforms.filter((u) => u.name.toLowerCase().includes(search.toLowerCase())),
    [uniforms, search]
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="rounded-xl bg-primary/15 p-3 text-primary"><Shirt className="h-7 w-7" /></div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Uniformes</h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-xl">
              Gerencie a grade do CT, sugira medidas automáticas por tamanho e venda diretamente para um aluno.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar uniforme..."
              className="pl-8 w-64"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Button onClick={() => { setEditing(null); setEditOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" /> Novo Uniforme
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Shirt} label="Itens cadastrados" value={String(uniforms.length)} />
        <StatCard icon={Package} label="Estoque total" value={String(uniforms.reduce((a, u) => a + (u.stock ?? 0), 0))} />
        <StatCard icon={ShoppingCart} label="Tamanhos" value={String(uniforms.reduce((a, u) => a + (u.uniform_sizes?.length ?? 0), 0))} />
        <StatCard icon={ShoppingCart} label="Alunos do CT" value={String(students.length)} />
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="overflow-hidden animate-pulse">
              <div className="aspect-square bg-muted" />
              <CardContent className="p-4 space-y-2">
                <div className="h-4 w-3/4 bg-muted rounded" />
                <div className="h-3 w-1/2 bg-muted rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 flex flex-col items-center text-center gap-3 border-dashed">
          <Shirt className="h-12 w-12 text-muted-foreground/40" />
          <h3 className="font-semibold">Nenhum uniforme cadastrado</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            Comece adicionando uma camisa ou calção. Ao escolher um tamanho, sugerimos as medidas automaticamente — você pode ajustar.
          </p>
          <Button onClick={() => { setEditing(null); setEditOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" /> Novo Uniforme
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((u) => (
            <UniformCard
              key={u.id}
              uniform={u}
              onEdit={() => { setEditing(u); setEditOpen(true); }}
              onDelete={async () => {
                if (!confirm(`Excluir "${u.name}"?`)) return;
                try { await delFn({ data: { id: u.id } }); toast.success("Excluído"); reload(); }
                catch (e: any) { toast.error(e?.message ?? "Erro"); }
              }}
              onSell={() => { setSaleUniform(u); setSaleOpen(true); }}
            />
          ))}
        </div>
      )}

      <UniformEditModal
        open={editOpen}
        onOpenChange={setEditOpen}
        editing={editing}
        tcId={tcId}
        existingUniforms={uniforms}
        onSave={async (payload) => {
          try {
            await saveFn({ data: payload });
            toast.success(payload.id ? "Atualizado" : "Cadastrado");
            setEditOpen(false);
            reload();
          } catch (e: any) { toast.error(e?.message ?? "Erro"); }
        }}
      />

      <SellModal
        open={saleOpen}
        onOpenChange={setSaleOpen}
        uniform={saleUniform}
        students={students}
        onSell={async (payload) => {
          try {
            const res: any = await sellFn({ data: payload });
            if (payload.payment_method === "pix_comprovante" && res?.id) {
              try {
                const { sendPixSaleWhatsapp } = await import("@/lib/api/pix-proofs.functions");
                await sendPixSaleWhatsapp({ data: { kind: "uniform", order_id: res.id } });
                toast.success("Venda registrada e instruções de PIX enviadas no WhatsApp do responsável.");
              } catch (e: any) {
                toast.warning(`Venda registrada, mas falha ao enviar WhatsApp: ${e?.message ?? "erro"}`);
              }
            } else {
              toast.success("Venda registrada!");
            }
            setSaleOpen(false);
            reload();
            const uni = saleUniform;
            if (res?.id && uni && !isPresencialMethod(payload.payment_method) && !isComprovanteMethod(payload.payment_method)) {
              const stu = payload.student_id ? students.find((s) => s.id === payload.student_id) : undefined;
              const payerName = stu?.name ?? (payload.buyer_name || "cliente");
              const net = Math.max(0, Number(payload.amount || 0) - Number(payload.discount || 0));
              const label = `${uni.name}${payload.size ? " — Tam. " + payload.size : ""}${payload.shirt_number ? " · Nº " + payload.shirt_number : ""}${payload.shirt_name ? " · " + payload.shirt_name : ""}`;
              setBilling({
                id: res.id,
                kind: "uniform",
                amount: net,
                itemLabel: label,
                payerName,
                phone: payload.buyer_phone || stu?.parent?.phone || null,
                payerEmail: stu?.parent?.email ?? null,
                paymentMethod: payload.payment_method,
              });
            }
          } catch (e: any) { toast.error(e?.message ?? "Erro"); }
        }}
      />

      <SaleWhatsappBillingModal
        open={!!billing}
        onOpenChange={(o) => !o && setBilling(null)}
        data={billing}
      />
    </div>
  );
}

function StatCard({ icon: Icon, label, value }: { icon: any; label: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-4 flex items-center gap-3">
        <div className="rounded-lg bg-primary/10 p-2 text-primary"><Icon className="h-5 w-5" /></div>
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground truncate">{label}</div>
          <div className="text-xl font-bold leading-tight">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function UniformCard({
  uniform, onEdit, onDelete, onSell,
}: { uniform: Uniform; onEdit: () => void; onDelete: () => void; onSell: () => void }) {
  const stockLow = (uniform.stock ?? 0) <= 5;
  return (
    <Card className="overflow-hidden group hover:shadow-lg hover:border-primary/40 transition-all">
      <div className="aspect-square bg-gradient-to-br from-muted via-background to-muted/40 relative overflow-hidden">
        {uniform.photo_url ? (
          <img src={uniform.photo_url} alt={uniform.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Shirt className="h-20 w-20 text-muted-foreground/25" />
          </div>
        )}
        {/* Floating cart for sale */}
        <button
          type="button"
          onClick={onSell}
          title="Vender para um aluno"
          className="absolute bottom-3 right-3 h-12 w-12 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:scale-110 active:scale-95 transition-transform"
        >
          <ShoppingCart className="h-5 w-5" />
        </button>
        <div className="absolute top-3 left-3 flex gap-1.5">
          <Badge variant="secondary" className="capitalize">{uniform.piece_type}</Badge>
        </div>
        <div className="absolute top-3 right-3 flex gap-1">
          <button onClick={onEdit} className="h-8 w-8 rounded-md bg-background/90 backdrop-blur flex items-center justify-center hover:bg-background border" title="Editar">
            <Edit className="h-4 w-4" />
          </button>
          <button onClick={onDelete} className="h-8 w-8 rounded-md bg-background/90 backdrop-blur flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground border" title="Excluir">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold leading-tight line-clamp-2">{uniform.name}</h3>
          <div className="text-right shrink-0">
            <div className="font-bold text-primary">{fmtMoney(Number(uniform.price))}</div>
            <div className={`text-[11px] ${stockLow ? "text-destructive" : "text-muted-foreground"}`}>
              Estoque: {uniform.stock ?? 0}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-1">
          {(uniform.uniform_sizes ?? []).slice(0, 8).map((s) => (
            <span key={s.id ?? s.size} className="text-[11px] px-1.5 py-0.5 rounded bg-muted border" title={s.width && s.height ? `${s.width}×${s.height} cm` : ""}>
              {s.size}
            </span>
          ))}
          {(uniform.uniform_sizes?.length ?? 0) > 8 && (
            <span className="text-[11px] px-1.5 py-0.5 rounded bg-muted border">+{uniform.uniform_sizes.length - 8}</span>
          )}
          {(!uniform.uniform_sizes || uniform.uniform_sizes.length === 0) && (
            <span className="text-[11px] text-muted-foreground italic">Sem tamanhos</span>
          )}
        </div>
        <Button variant="outline" className="w-full" onClick={onSell}>
          <ShoppingCart className="h-4 w-4 mr-2" /> Vender
        </Button>
      </CardContent>
    </Card>
  );
}

function UniformEditModal({
  open, onOpenChange, editing, tcId, onSave, existingUniforms,
}: {
  open: boolean; onOpenChange: (v: boolean) => void; editing: Uniform | null; tcId: string | null;
  onSave: (p: any) => Promise<void>;
  existingUniforms?: Uniform[];
}) {
  const [name, setName] = useState("");
  const [pieceType, setPieceType] = useState("camisa");
  const [price, setPrice] = useState<string>("0");
  const [stock, setStock] = useState<string>("0");
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [sizes, setSizes] = useState<SizeRow[]>([]);
  const [acceptedMethods, setAcceptedMethods] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [maxJersey, setMaxJersey] = useState<string>("");
  const [requireName, setRequireName] = useState(false);
  const [requireNumber, setRequireNumber] = useState(false);
  const [jerseyUniqueness, setJerseyUniqueness] = useState<"none" | "category" | "ct">("ct");
  const [leadDays, setLeadDays] = useState<string>("0");
  const [customTypes, setCustomTypes] = useState<{ value: string; label: string }[]>([]);
  const [newTypeOpen, setNewTypeOpen] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");
  const [meiaoClassByIndex, setMeiaoClassByIndex] = useState<Record<number, string>>({});

  const pieceTypeOptions = useMemo(() => {
    const map = new Map<string, string>();
    PIECE_TYPES.forEach((p) => map.set(p.value, p.label));
    (existingUniforms ?? []).forEach((u) => {
      if (u.piece_type && !map.has(u.piece_type)) {
        map.set(u.piece_type, u.piece_type.charAt(0).toUpperCase() + u.piece_type.slice(1));
      }
    });
    customTypes.forEach((t) => { if (!map.has(t.value)) map.set(t.value, t.label); });
    if (pieceType && !map.has(pieceType)) map.set(pieceType, pieceType);
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [existingUniforms, customTypes, pieceType]);

  const isMeiao = pieceType === "meiao";
  const isJerseyNumbered = pieceType === "camisa" || pieceType === "calcao" || pieceType === "camisa_short";

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setName(editing.name);
      setPieceType(editing.piece_type);
      setPrice(String(editing.price ?? 0));
      setStock(String(editing.stock ?? 0));
      setPhotoUrl(editing.photo_url);
      setSizes((editing.uniform_sizes ?? []).map((s: any) => ({ id: s.id, size: s.size, width: s.width, height: s.height, stock: s.stock ?? 0 })));
      setAcceptedMethods(editing.accepted_payment_methods ?? []);
      setMaxJersey(editing.max_jersey_number != null ? String(editing.max_jersey_number) : "");
      setRequireName(!!editing.require_jersey_name);
      setRequireNumber(!!editing.require_jersey_number);
      setJerseyUniqueness((editing.jersey_uniqueness as any) ?? "ct");
      setLeadDays(String((editing as any).delivery_lead_days ?? 0));
      const inferredClasses: Record<number, string> = {};
      (editing.uniform_sizes ?? []).forEach((s: any, i: number) => {
        const found = MEIAO_CLASSES.find((c) => c.full === s.size);
        if (found) inferredClasses[i] = found.value;
      });
      setMeiaoClassByIndex(inferredClasses);
    } else {
      setName(""); setPieceType("camisa"); setPrice("0"); setStock("0"); setPhotoUrl(null); setSizes([]);
      setAcceptedMethods(PAYMENT_METHODS.map((m) => m.value));
      setMaxJersey(""); setRequireName(false); setRequireNumber(false); setJerseyUniqueness("ct");
      setLeadDays("0"); setMeiaoClassByIndex({});
    }
  }, [open, editing]);

  const addSize = (sz?: string) => {
    const size = sz ?? "";
    const sug = size ? suggest(pieceType, size) : null;
    setSizes((arr) => [...arr, { size, width: sug?.width ?? null, height: sug?.height ?? null, stock: 0 }]);
  };

  const addAllSuggested = () => {
    const guide = SIZE_GUIDE[pieceType];
    if (!guide) { toast.error("Tipo não possui tabela de medidas"); return; }
    const existing = new Set(sizes.map((s) => s.size));
    const additions: SizeRow[] = [];
    for (const sz of SIZES_CATALOG) {
      if (existing.has(sz)) continue;
      const g = guide[sz];
      if (g) additions.push({ size: sz, width: g.width, height: g.height, stock: 0 });
    }
    setSizes((arr) => [...arr, ...additions]);
  };

  const updateSize = (i: number, patch: Partial<SizeRow>) => {
    setSizes((arr) => arr.map((s, idx) => {
      if (idx !== i) return s;
      const next = { ...s, ...patch };
      // if size changed, auto-suggest (overwrites previous suggestion)
      if (patch.size !== undefined && next.size) {
        const sug = suggest(pieceType, next.size);
        if (sug) { next.width = sug.width; next.height = sug.height; }
      }
      return next;
    }));
  };

  const reapplySuggestions = () => {
    setSizes((arr) => arr.map((s) => {
      const sug = s.size ? suggest(pieceType, s.size) : null;
      return sug ? { ...s, width: sug.width, height: sug.height } : s;
    }));
  };

  const handlePhotoUpload = async (file: File) => {
    if (!tcId) { toast.error("Cadastre o CT primeiro"); return; }
    try {
      setUploading(true);
      const ext = (file.name.split(".").pop() ?? "jpg").toLowerCase();
      const path = `${tcId}/${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage.from("uniforms").upload(path, file, { contentType: file.type });
      if (error) throw error;
      // bucket is private — use a long-lived signed URL
      const { data, error: sErr } = await supabase.storage.from("uniforms").createSignedUrl(path, 60 * 60 * 24 * 365);
      if (sErr) throw sErr;
      setPhotoUrl(data.signedUrl);
    } catch (e: any) {
      toast.error(e?.message ?? "Erro no upload");
    } finally { setUploading(false); }
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Nome obrigatório"); return; }
    const validSizes = sizes.filter((s) => s.size.trim());
    setSaving(true);
    try {
      await onSave({
        id: editing?.id ?? null,
        name: name.trim(),
        piece_type: pieceType,
        price: Number(price) || 0,
        stock: parseInt(stock || "0", 10) || 0,
        photo_url: photoUrl,
        accepted_payment_methods: acceptedMethods,
        max_jersey_number: maxJersey.trim() === "" ? null : Math.max(1, parseInt(maxJersey, 10) || 0),
        require_jersey_name: requireName,
        require_jersey_number: requireNumber,
        jersey_uniqueness: jerseyUniqueness,
        delivery_lead_days: Math.max(0, parseInt(leadDays || "0", 10) || 0),
        sizes: validSizes.map((s) => ({
          id: s.id ?? null, size: s.size.trim(),
          width: s.width != null ? Number(s.width) : null,
          height: s.height != null ? Number(s.height) : null,
          stock: Number.isFinite(Number(s.stock)) ? Math.max(0, Number(s.stock)) : 0,
        })),
      });
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Editar Uniforme" : "Novo Uniforme"}</DialogTitle>
          <DialogDescription>
            Ao escolher um tamanho conhecido, as medidas são sugeridas automaticamente. Você pode editar à vontade.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-5">
          {/* Photo */}
          <div className="space-y-2">
            <Label>Foto</Label>
            <div className="aspect-square rounded-lg border-2 border-dashed bg-muted/30 relative flex items-center justify-center overflow-hidden">
              {photoUrl ? (
                <>
                  <img src={photoUrl} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setPhotoUrl(null)} className="absolute top-1 right-1 h-7 w-7 rounded-full bg-background/90 border flex items-center justify-center"><X className="h-4 w-4" /></button>
                </>
              ) : (
                <Shirt className="h-12 w-12 text-muted-foreground/40" />
              )}
            </div>
            <label className="block">
              <input type="file" accept="image/*" className="hidden" disabled={uploading}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handlePhotoUpload(f); e.target.value = ""; }} />
              <span className="inline-flex w-full items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer hover:bg-muted">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {uploading ? "Enviando..." : "Enviar foto"}
              </span>
            </label>
          </div>

          {/* Fields */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label>Nome do Uniforme *</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Camisa Oficial 2026" />
              </div>
              <div>
                <Label>Tipo de Peça *</Label>
                <div className="flex items-center gap-2">
                  <Select value={pieceType} onValueChange={(v) => { setPieceType(v); }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {pieceTypeOptions.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button type="button" size="icon" variant="outline" title="Novo tipo de peça" onClick={() => { setNewTypeName(""); setNewTypeOpen(true); }}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div>
                <Label>Preço (R$) *</Label>
                <Input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} />
              </div>
              <div>
                <Label>Estoque Total</Label>
                <Input type="number" min="0" value={stock} onChange={(e) => setStock(e.target.value)} />
                <p className="text-[11px] text-muted-foreground mt-1">Total geral. Use a coluna "Estoque" abaixo para definir o estoque por tamanho.</p>
              </div>
              <div>
                <Label>Prazo de Entrega (dias)</Label>
                <Input type="number" min="0" value={leadDays} onChange={(e) => setLeadDays(e.target.value)} placeholder="0" />
                <p className="text-[11px] text-muted-foreground mt-1">Usado para calcular a data prevista em vendas com entrega futura.</p>
              </div>
            </div>

            {/* Sizes */}
            <div className="rounded-lg border bg-muted/20 p-3 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h4 className="font-semibold">Grade de Tamanhos</h4>
                  <p className="text-xs text-muted-foreground">
                    {SIZE_GUIDE[pieceType] ? "As medidas são sugeridas pelo tamanho — edite se necessário." : "Este tipo não tem tabela de sugestão."}
                  </p>
                </div>
                <div className="flex gap-2">
                  {SIZE_GUIDE[pieceType] && (
                    <>
                      <Button type="button" size="sm" variant="outline" onClick={reapplySuggestions}>Aplicar sugestões</Button>
                      <Button type="button" size="sm" variant="outline" onClick={addAllSuggested}>+ Todos os tamanhos</Button>
                    </>
                  )}
                  <Button type="button" size="sm" onClick={() => addSize()}><Plus className="h-3.5 w-3.5 mr-1" />Adicionar</Button>
                </div>
              </div>

              <div className="space-y-2">
                {sizes.length === 0 && (
                  <div className="text-xs text-muted-foreground text-center py-4">
                    Nenhum tamanho. Clique em "+ Todos os tamanhos" para começar.
                  </div>
                )}
                {sizes.length > 0 && (
                  <div className={isMeiao
                    ? "grid grid-cols-[1fr_1fr_0.8fr_auto] gap-2 text-[11px] uppercase text-muted-foreground px-1"
                    : "grid grid-cols-[1.2fr_1fr_1fr_0.8fr_auto] gap-2 text-[11px] uppercase text-muted-foreground px-1"}>
                    {isMeiao ? (
                      <>
                        <span>Classificação</span>
                        <span>Tamanho</span>
                        <span>Estoque</span>
                        <span />
                      </>
                    ) : (
                      <>
                        <span>Tamanho</span>
                        <span>{pieceType === "calcao" ? "Cintura" : "Largura"}</span>
                        <span>Comprimento</span>
                        <span>Estoque</span>
                        <span />
                      </>
                    )}
                  </div>
                )}
                {sizes.map((s, i) => (
                  isMeiao ? (
                    <div key={i} className="grid grid-cols-[1fr_1fr_0.8fr_auto] gap-2 items-center">
                      <Select
                        value={meiaoClassByIndex[i] ?? undefined}
                        onValueChange={(v) => {
                          setMeiaoClassByIndex((m) => ({ ...m, [i]: v }));
                          const cls = MEIAO_CLASSES.find((c) => c.value === v);
                          updateSize(i, { size: cls?.full ?? "" });
                        }}
                      >
                        <SelectTrigger><SelectValue placeholder="Classificação" /></SelectTrigger>
                        <SelectContent>
                          {MEIAO_CLASSES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Select
                        value={meiaoClassByIndex[i] ? s.size || undefined : undefined}
                        onValueChange={(v) => updateSize(i, { size: v })}
                        disabled={!meiaoClassByIndex[i]}
                      >
                        <SelectTrigger><SelectValue placeholder="Tamanho" /></SelectTrigger>
                        <SelectContent>
                          {meiaoClassByIndex[i] && (() => {
                            const cls = MEIAO_CLASSES.find((c) => c.value === meiaoClassByIndex[i]);
                            return cls ? <SelectItem value={cls.full}>{cls.full}</SelectItem> : null;
                          })()}
                        </SelectContent>
                      </Select>
                      <Input type="number" min="0" placeholder="Estoque" title="Estoque deste tamanho"
                        value={s.stock ?? 0} onChange={(e) => updateSize(i, { stock: e.target.value === "" ? 0 : Math.max(0, Number(e.target.value)) })} />
                      <Button type="button" size="icon" variant="ghost" onClick={() => setSizes((arr) => arr.filter((_, idx) => idx !== i))}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ) : (
                    <div key={i} className="grid grid-cols-[1.2fr_1fr_1fr_0.8fr_auto] gap-2 items-center">
                      <div>
                        {SIZE_GUIDE[pieceType] ? (
                          <Select value={s.size || undefined} onValueChange={(v) => updateSize(i, { size: v })}>
                            <SelectTrigger><SelectValue placeholder="Tamanho" /></SelectTrigger>
                            <SelectContent>
                              {SIZES_CATALOG.map((sz) => {
                                const lbl = SIZE_GUIDE[pieceType]?.[sz]?.label;
                                return <SelectItem key={sz} value={sz}>{lbl ?? sz}</SelectItem>;
                              })}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input placeholder="Tamanho" value={s.size} onChange={(e) => updateSize(i, { size: e.target.value })} />
                        )}
                      </div>
                      <Input type="number" placeholder={pieceType === "calcao" ? "Cintura (cm)" : "Largura (cm)"}
                        value={s.width ?? ""} onChange={(e) => updateSize(i, { width: e.target.value === "" ? null : Number(e.target.value) })} />
                      <Input type="number" placeholder="Comprimento (cm)"
                        value={s.height ?? ""} onChange={(e) => updateSize(i, { height: e.target.value === "" ? null : Number(e.target.value) })} />
                      <Input type="number" min="0" placeholder="Estoque" title="Estoque deste tamanho"
                        value={s.stock ?? 0} onChange={(e) => updateSize(i, { stock: e.target.value === "" ? 0 : Math.max(0, Number(e.target.value)) })} />
                      <Button type="button" size="icon" variant="ghost" onClick={() => setSizes((arr) => arr.filter((_, idx) => idx !== i))}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  )
                ))}
              </div>
            </div>

            {isJerseyNumbered && (
              <div className="rounded-lg border bg-muted/20 p-3 space-y-3">
                <div>
                  <h4 className="font-semibold">Regras de Numeração {pieceType === "calcao" ? "do Calção" : "da Camisa"}</h4>
                  <p className="text-xs text-muted-foreground">
                    Defina o número máximo permitido e onde ele deve ser único.
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Até qual número? (máximo)</Label>
                    <Input
                      type="number" min="1" max="999"
                      value={maxJersey}
                      onChange={(e) => setMaxJersey(e.target.value)}
                      placeholder="Ex: 30"
                    />
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Em branco = sem limite (1 a 99).
                    </p>
                  </div>
                  <div>
                    <Label>Repetição do número</Label>
                    <label className="mt-2 flex items-start gap-2 rounded-md border bg-background p-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={jerseyUniqueness === "none"}
                        onChange={(e) => setJerseyUniqueness(e.target.checked ? "none" : "ct")}
                      />
                      <span className="text-sm">
                        <strong>Permitir número repetido entre atletas</strong>
                        <div className="text-[11px] text-muted-foreground">
                          Marcado: qualquer número é aceito na venda. Desmarcado: só ficam disponíveis os números que ainda não pertencem a atletas ativos.
                        </div>
                      </span>
                    </label>
                    {jerseyUniqueness !== "none" && (
                      <div className="space-y-2 pt-2">
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input type="radio" className="mt-1" name="uniq" checked={jerseyUniqueness === "category"} onChange={() => setJerseyUniqueness("category")} />
                          <span className="text-sm"><strong>Único por categoria</strong> — não repete dentro da mesma categoria</span>
                        </label>
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input type="radio" className="mt-1" name="uniq" checked={jerseyUniqueness === "ct"} onChange={() => setJerseyUniqueness("ct")} />
                          <span className="text-sm"><strong>Único no CT</strong> — não repete em todo o Centro de Treinamento</span>
                        </label>
                      </div>
                    )}
                  </div>

                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                  <label className="flex items-center justify-between gap-2 rounded-md border bg-background p-2">
                    <div>
                      <div className="text-sm font-medium">Nome obrigatório</div>
                      <div className="text-[11px] text-muted-foreground">Exigir nome na peça ao vender</div>
                    </div>
                    <Switch checked={requireName} onCheckedChange={setRequireName} />
                  </label>
                  <label className="flex items-center justify-between gap-2 rounded-md border bg-background p-2">
                    <div>
                      <div className="text-sm font-medium">Número obrigatório</div>
                      <div className="text-[11px] text-muted-foreground">Exigir número na peça ao vender</div>
                    </div>
                    <Switch checked={requireNumber} onCheckedChange={setRequireNumber} />
                  </label>
                </div>
              </div>
            )}

            <PaymentMethodsPicker value={acceptedMethods} onChange={setAcceptedMethods} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Salvar Uniforme
          </Button>
        </DialogFooter>
      </DialogContent>

      <Dialog open={newTypeOpen} onOpenChange={setNewTypeOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Novo tipo de peça</DialogTitle>
            <DialogDescription>Cadastre um tipo de peça além dos padrões.</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Nome do tipo</Label>
            <Input value={newTypeName} onChange={(e) => setNewTypeName(e.target.value)} placeholder="Ex: Jaqueta" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewTypeOpen(false)}>Cancelar</Button>
            <Button
              onClick={() => {
                const label = newTypeName.trim();
                if (!label) { toast.error("Informe um nome"); return; }
                const value = label.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || label.toLowerCase();
                setCustomTypes((arr) => arr.some((t) => t.value === value) ? arr : [...arr, { value, label }]);
                setPieceType(value);
                setNewTypeOpen(false);
              }}
            >
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

function SellModal({
  open, onOpenChange, uniform, students, onSell,
}: {
  open: boolean; onOpenChange: (v: boolean) => void;
  uniform: Uniform | null; students: Student[];
  onSell: (p: any) => Promise<void>;
}) {
  const availableFn = useServerFn(listAvailableJerseyNumbers);
  const allowedMethods = useMemo(() => {
    const accepted = uniform?.accepted_payment_methods ?? [];
    const list = accepted.length > 0
      ? PAYMENT_METHODS.filter((m) => accepted.includes(m.value))
      : PAYMENT_METHODS;
    return list;
  }, [uniform]);

  const [studentId, setStudentId] = useState<string>("");
  const [walkIn, setWalkIn] = useState<boolean>(false);
  const [buyerName, setBuyerName] = useState<string>("");
  const [buyerCpf, setBuyerCpf] = useState<string>("");
  const [buyerPhone, setBuyerPhone] = useState<string>("");
  const [cpfLookupHint, setCpfLookupHint] = useState<string>("");
  const [size, setSize] = useState<string>("");
  const [amount, setAmount] = useState<string>("0");
  const [discount, setDiscount] = useState<string>("0");
  const [paymentMethod, setPaymentMethod] = useState<string>("pix");
  const [dueDate, setDueDate] = useState<string>("");
  const [deliveryType, setDeliveryType] = useState<"imediata" | "futura">("imediata");
  const [expectedDelivery, setExpectedDelivery] = useState<string>("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [shirtName, setShirtName] = useState("");
  const [shirtNumber, setShirtNumber] = useState<string>(""); // "" = sem número
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [jerseyInfo, setJerseyInfo] = useState<{ max: number; taken: number[]; uniqueness: string } | null>(null);
  const [loadingJersey, setLoadingJersey] = useState(false);
  const lookupCpfFn = useServerFn(lookupBuyerByCpf);

  useEffect(() => {
    if (!open || !uniform) return;
    setStudentId(""); setWalkIn(false); setBuyerName(""); setBuyerCpf(""); setBuyerPhone(""); setCpfLookupHint("");
    setSize(uniform.uniform_sizes?.[0]?.size ?? "");
    setAmount(String(uniform.price ?? 0));
    setDiscount("0");
    setPaymentMethod(allowedMethods[0]?.value ?? "pix");
    setDueDate("");
    // Padrão: entrega futura conforme o prazo cadastrado do uniforme (ex.: 30 dias)
    const lead = Number((uniform as any)?.delivery_lead_days ?? 0) || 0;
    if (lead > 0) {
      setDeliveryType("futura");
      setExpectedDelivery(brIsoDate(new Date(Date.now() + lead * 86400000)));
    } else {
      setDeliveryType("imediata");
      setExpectedDelivery("");
    }

    setShirtName(""); setShirtNumber(""); setSearch("");
    setJerseyInfo(null);
  }, [open, uniform, allowedMethods]);

  // Auto-preenche a previsão de entrega ao escolher "futura"
  useEffect(() => {
    if (deliveryType !== "futura") return;
    if (expectedDelivery) return;
    const days = (uniform as any)?.delivery_lead_days ?? 0;
    setExpectedDelivery(brIsoDate(new Date(Date.now() + (Number(days) || 0) * 86400000)));
  }, [deliveryType, uniform, expectedDelivery]);

  // Lookup do CPF do responsável ao digitar em venda avulsa
  useEffect(() => {
    if (!walkIn) { setCpfLookupHint(""); return; }
    const digits = buyerCpf.replace(/\D/g, "");
    if (digits.length !== 11) { setCpfLookupHint(""); return; }
    let cancel = false;
    lookupCpfFn({ data: { cpf: digits } })
      .then((res: any) => {
        if (cancel) return;
        if (res?.match) {
          setCpfLookupHint(`✓ Vinculado a ${res.match.parent_name}${res.match.student_name ? " — aluno " + res.match.student_name : ""}`);
          if (res.match.student_id) { setStudentId(res.match.student_id); setWalkIn(false); }
          else if (!buyerName) setBuyerName(res.match.parent_name);
        } else {
          setCpfLookupHint("CPF não encontrado no cadastro — seguirá como venda avulsa.");
        }
      }).catch(() => {});
    return () => { cancel = true; };
  }, [buyerCpf, walkIn, lookupCpfFn, buyerName]);


  // Recarrega lista de números disponíveis quando muda aluno (categoria)
  useEffect(() => {
    if (!open || !uniform) return;
    if (!["camisa", "calcao", "camisa_short"].includes(uniform.piece_type)) return;
    let cancel = false;
    setLoadingJersey(true);
    availableFn({ data: { uniform_id: uniform.id, student_id: studentId || null } })
      .then((res: any) => { if (!cancel) setJerseyInfo(res); })
      .catch(() => {})
      .finally(() => { if (!cancel) setLoadingJersey(false); });
    return () => { cancel = true; };
  }, [open, uniform, studentId, availableFn]);

  const filteredStudents = useMemo(
    () => students.filter((s) => s.name.toLowerCase().includes(search.toLowerCase())),
    [students, search]
  );

  if (!uniform) return null;

  const isCamisa = uniform.piece_type === "camisa" || uniform.piece_type === "calcao" || uniform.piece_type === "camisa_short";

  const validateBeforeConfirm = () => {
    if (!walkIn && !studentId) { toast.error("Escolha um aluno ou marque venda avulsa"); return false; }
    if (walkIn && !buyerName.trim()) { toast.error("Informe o nome do comprador"); return false; }
    if (!size) { toast.error("Escolha um tamanho"); return false; }
    if (isCamisa) {
      if (uniform.require_jersey_name && !shirtName.trim()) {
        toast.error("Nome na camisa é obrigatório."); return false;
      }
      if (uniform.require_jersey_number && !shirtNumber.trim()) {
        toast.error("Número da camisa é obrigatório."); return false;
      }
      const uniqueness = (jerseyInfo?.uniqueness ?? uniform.jersey_uniqueness ?? "ct");
      if (uniqueness !== "none" && shirtNumber.trim()) {
        const n = parseInt(shirtNumber, 10);
        if (jerseyInfo?.taken?.includes(n)) {
          toast.error(`Já existe atleta ativo com o número ${n}. Escolha outro.`);
          return false;
        }
      }
    }



    if (deliveryType === "futura" && !expectedDelivery) {
      toast.error("Informe a data prevista de entrega"); return false;
    }
    return true;
  };

  const handleSell = async () => {
    setSaving(true);
    try {
      await onSell({
        uniform_id: uniform.id,
        student_id: walkIn ? null : studentId,
        buyer_name: walkIn ? buyerName.trim() : null,
        buyer_cpf: walkIn ? buyerCpf.replace(/\D/g, "") || null : null,
        buyer_phone: walkIn ? buyerPhone.replace(/\D/g, "") || null : null,
        size,
        amount: Number(amount) || 0,
        discount: Number(discount) || 0,
        payment_method: paymentMethod,
        due_date: dueDate || null, status: "pago",
        shirt_name: isCamisa ? (shirtName || null) : null,
        shirt_number: isCamisa && shirtNumber ? shirtNumber : null,
        delivery_type: deliveryType,
        expected_delivery_date: deliveryType === "futura" ? expectedDelivery : null,
      });
      setConfirmOpen(false);
    } finally { setSaving(false); }
  };


  const selectedStudent = students.find((s) => s.id === studentId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShoppingCart className="h-5 w-5" /> Vender uniforme</DialogTitle>
          <DialogDescription>Registre a venda para um aluno do seu Centro de Treinamento.</DialogDescription>
        </DialogHeader>

        {/* Product summary */}
        <div className="rounded-lg border bg-muted/20 p-3 flex items-center gap-3">
          <div className="h-16 w-16 rounded-md bg-muted overflow-hidden flex items-center justify-center shrink-0">
            {uniform.photo_url ? <img src={uniform.photo_url} alt="" className="w-full h-full object-cover" /> : <Shirt className="h-8 w-8 text-muted-foreground/40" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold truncate">{uniform.name}</div>
            <div className="text-xs text-muted-foreground capitalize">{uniform.piece_type}</div>
          </div>
          <div className="font-bold text-primary">{fmtMoney(Number(uniform.price))}</div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Student picker / walk-in */}
          <div className="sm:col-span-2 space-y-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={walkIn}
                onChange={(e) => { setWalkIn(e.target.checked); if (e.target.checked) setStudentId(""); }}
              />
              <span>Venda avulsa (sem aluno cadastrado)</span>
            </label>

            {walkIn ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label>Nome do comprador *</Label>
                  <Input
                    placeholder="Ex.: João da Silva"
                    value={buyerName}
                    onChange={(e) => setBuyerName(e.target.value)}
                  />
                </div>
                <div>
                  <Label>CPF (opcional)</Label>
                  <Input
                    placeholder="Buscar responsável cadastrado"
                    value={buyerCpf}
                    onChange={(e) => setBuyerCpf(e.target.value)}
                  />
                  {cpfLookupHint && (
                    <p className="text-[11px] text-muted-foreground mt-1">{cpfLookupHint}</p>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <Label>WhatsApp (opcional)</Label>
                  <Input
                    placeholder="DDD + número (para envio de cobrança)"
                    value={buyerPhone}
                    onChange={(e) => setBuyerPhone(e.target.value)}
                  />
                </div>
              </div>
            ) : (
              <div>
                <Label>Aluno *</Label>
                {selectedStudent ? (
                  <div className="flex items-center gap-2 rounded-md border p-2">
                    <div className="h-8 w-8 rounded-full bg-muted overflow-hidden flex items-center justify-center">
                      {selectedStudent.photo_url ? <img src={selectedStudent.photo_url} alt="" className="w-full h-full object-cover" /> : selectedStudent.name[0]}
                    </div>
                    <div className="flex-1 font-medium">{selectedStudent.name}</div>
                    <Button variant="ghost" size="sm" onClick={() => setStudentId("")}>Trocar</Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input className="pl-8" placeholder="Buscar aluno..." value={search} onChange={(e) => setSearch(e.target.value)} />
                    </div>
                    <div className="max-h-40 overflow-y-auto rounded-md border divide-y">
                      {filteredStudents.length === 0 ? (
                        <div className="text-xs text-muted-foreground p-3 text-center">Nenhum aluno encontrado</div>
                      ) : filteredStudents.map((s) => (
                        <button key={s.id} type="button" onClick={() => setStudentId(s.id)}
                          className="w-full text-left p-2 hover:bg-muted flex items-center gap-2">
                          <div className="h-7 w-7 rounded-full bg-muted overflow-hidden flex items-center justify-center text-xs">
                            {s.photo_url ? <img src={s.photo_url} alt="" className="w-full h-full object-cover" /> : s.name[0]}
                          </div>
                          <span className="text-sm">{s.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <Label>Tamanho *</Label>
            <Select value={size || undefined} onValueChange={setSize}>
              <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>
                {(uniform.uniform_sizes ?? []).map((s) => (
                  <SelectItem key={s.id ?? s.size} value={s.size}>
                    {s.size}{s.width && s.height ? ` — ${s.width}×${s.height} cm` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Valor (R$) *</Label>
            <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>

          <div>
            <Label>Desconto (R$)</Label>
            <Input type="number" min="0" step="0.01" value={discount} onChange={(e) => setDiscount(e.target.value)} />
            <p className="text-[11px] text-muted-foreground mt-1">
              Total: <strong>{fmtMoney(Math.max(0, (Number(amount)||0) - (Number(discount)||0)))}</strong>
            </p>
          </div>

          <div>
            <Label>Forma de Pagamento</Label>
            <Select value={paymentMethod} onValueChange={(v) => setPaymentMethod(v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {allowedMethods.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    <span className="flex items-center gap-1.5">
                      {m.label}
                      {isMercadoPagoMethod(m.value) && <MercadoPagoBadge />}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="sm:col-span-2 rounded-md bg-muted/30 border p-2 text-xs text-muted-foreground">
            {isPresencialMethod(paymentMethod)
              ? "💵 Recebimento presencial — entra direto no Caixa como pago."
              : isComprovanteMethod(paymentMethod)
              ? "📲 Será enviada a chave PIX no WhatsApp do responsável. Confirmar via comprovante."
              : isMercadoPagoMethod(paymentMethod)
              ? "🔵 Cobrança gerada via Mercado Pago — confirmação automática após pagamento."
              : "⏳ Esta venda ficará pendente até ser confirmada em Confirmação de Pagamentos."}
          </div>

          <div>
            <Label>Entrega *</Label>
            <Select value={deliveryType} onValueChange={(v) => setDeliveryType(v as any)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="imediata">Imediata (entregar agora)</SelectItem>
                <SelectItem value="futura">Entrega futura</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {deliveryType === "futura" && (
            <div>
              <Label>Previsão de entrega *</Label>
              <Input type="date" value={expectedDelivery} onChange={(e) => setExpectedDelivery(e.target.value)} />
            </div>
          )}



          {isCamisa && (
            <>
              <div>
                <Label>
                  Nome na Camisa {uniform.require_jersey_name && <span className="text-destructive">*</span>}
                </Label>
                <Input
                  value={shirtName}
                  onChange={(e) => setShirtName(e.target.value)}
                  placeholder={uniform.require_jersey_name ? "Obrigatório" : "Opcional"}
                />
              </div>
              <div>
                <Label>
                  Número {uniform.require_jersey_number && <span className="text-destructive">*</span>}
                </Label>
                {(() => {
                  const max = jerseyInfo?.max ?? uniform.max_jersey_number ?? 999;
                  const uniqueness = (jerseyInfo?.uniqueness ?? uniform.jersey_uniqueness ?? "ct") as "none"|"category"|"ct";
                  const taken = new Set(jerseyInfo?.taken ?? []);
                  const free = uniqueness === "none";
                  const typedDigits = (shirtNumber || "").replace(/\D/g, "").slice(0, 3);
                  const typedNum = typedDigits ? parseInt(typedDigits, 10) : null;
                  const isTaken = !free && typedNum != null && taken.has(typedNum);

                  // Sugestões: até 20 números disponíveis (ou todos, se livre) que começam com o digitado
                  let suggestions: number[] = [];
                  if (typedDigits) {
                    const upTo = Math.min(max, 999);
                    for (let n = 1; n <= upTo && suggestions.length < 20; n++) {
                      if (!String(n).startsWith(typedDigits)) continue;
                      if (!free && taken.has(n)) continue;
                      suggestions.push(n);
                    }
                  }

                  return (
                    <div className="relative">
                      <Input
                        inputMode="numeric"
                        maxLength={3}
                        placeholder={free ? "Qualquer número (livre)" : "Digite para ver disponíveis…"}
                        value={shirtNumber}
                        onChange={(e) => {
                          const only = e.target.value.replace(/\D/g, "").slice(0, 3);
                          setShirtNumber(only);
                        }}
                        aria-invalid={isTaken}
                        className={isTaken ? "border-destructive focus-visible:ring-destructive" : ""}
                      />
                      {typedDigits && !free && suggestions.length > 0 && (
                        <div className="absolute z-30 mt-1 w-full rounded-md border bg-popover shadow-lg max-h-56 overflow-y-auto">
                          {suggestions.map((n) => (
                            <button
                              key={n}
                              type="button"
                              className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted"
                              onMouseDown={(ev) => { ev.preventDefault(); setShirtNumber(String(n)); }}
                            >
                              {n}
                            </button>
                          ))}
                        </div>
                      )}
                      {isTaken && (
                        <p className="text-[11px] text-destructive mt-1">
                          Já existe atleta ativo com o número {typedNum}. Escolha outro número.
                        </p>
                      )}
                      {!free && !isTaken && typedDigits && suggestions.length === 0 && (
                        <p className="text-[11px] text-muted-foreground mt-1">
                          Nenhum número disponível começando por {typedDigits}.
                        </p>
                      )}
                      {jerseyInfo && (
                        <p className="text-[11px] text-muted-foreground mt-1">
                          Máximo: {jerseyInfo.max} · Regra: {uniqueness === "none" ? "livre (repetição permitida)" : uniqueness === "category" ? "único por categoria" : "único no CT"}
                          {loadingJersey && " · atualizando…"}
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>

            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={() => { if (validateBeforeConfirm()) setConfirmOpen(true); }} disabled={saving}>
            <ShoppingCart className="h-4 w-4 mr-2" /> Vender
          </Button>
        </DialogFooter>
      </DialogContent>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="uppercase tracking-wide">CONFIRMAR TIPO DE ENTREGA</AlertDialogTitle>
            <AlertDialogDescription>
              Esta venda será registrada como <strong>{deliveryType === "imediata" ? "ENTREGA IMEDIATA" : "ENTREGA FUTURA"}</strong>
              {deliveryType === "futura" && expectedDelivery ? ` prevista para ${new Date(expectedDelivery + "T00:00:00").toLocaleDateString("pt-BR")}` : ""}.
              Deseja confirmar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button type="button" variant={deliveryType === "imediata" ? "default" : "outline"} className="flex-1" onClick={() => setDeliveryType("imediata")}>
              Imediata
            </Button>
            <Button type="button" variant={deliveryType === "futura" ? "default" : "outline"} className="flex-1" onClick={() => setDeliveryType("futura")}>
              Futura
            </Button>
          </div>
          {deliveryType === "futura" && (
            <div>
              <Label>Previsão de entrega</Label>
              <Input type="date" value={expectedDelivery} onChange={(e) => setExpectedDelivery(e.target.value)} />
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleSell} disabled={saving || (deliveryType === "futura" && !expectedDelivery)}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Confirmar venda
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}


